/**
 * Tagged stock by location and item, split by quality status (LSK, 2026-11-02).
 *
 * Every tag the system holds in stock, read live from Smartlink. A unit whose quality was never
 * changed on the handheld is Good Quality.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { Card, Table, Loading, cell } from '../components/ui';

type Row = {
  location: string;
  sku: string;
  description: string | null;
  total: number;
  quality: Record<string, number>;
};

export default function QualityInventory() {
  const [location, setLocation] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const inv = useQuery({
    queryKey: ['quality-inventory', location],
    queryFn: () =>
      api.get<{
        statuses: Array<{ code: number; label: string }>;
        locations: Array<{ code: string; name: string; inStock: number }>;
        rows: Row[];
      }>(
        '/api/v1/app/quality/inventory' +
          (location ? `?location=${encodeURIComponent(location)}` : ''),
      ),
  });

  const all = (inv.data?.statuses ?? []).map((s) => s.label);
  const good = all[0] ?? 'Good Quality';
  const rows = (inv.data?.rows ?? []).filter(
    (r) => !onlyIssues || (r.quality[good] ?? 0) < r.total,
  );
  const labels = all.filter((l) => rows.some((r) => (r.quality[l] ?? 0) > 0));
  const totals: Record<string, number> = {};
  for (const r of rows)
    for (const [k, v] of Object.entries(r.quality)) totals[k] = (totals[k] ?? 0) + v;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">Quality by location</h1>
      <Card
        title="Tagged stock by quality status"
        subtitle="Every tag in stock, live from Smartlink. A unit never changed on the handheld is Good Quality."
        actions={
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              <option value="">All locations</option>
              {(inv.data?.locations ?? []).map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code} — {l.name} ({l.inStock})
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={onlyIssues}
                onChange={(e) => setOnlyIssues(e.target.checked)}
              />
              Only items with a unit not Good
            </label>
          </div>
        }
      >
        {inv.isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2 text-xs">
              {labels.map((x) => (
                <span key={x} className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  {x}: <strong>{totals[x]?.toLocaleString()}</strong>
                </span>
              ))}
            </div>
            <Table
              head={['Location', 'Item', 'Description', 'Units', ...labels]}
              rows={rows.map((r) => [
                r.location,
                <span className="font-mono text-xs">{r.sku}</span>,
                r.description ?? '—',
                cell(r.total, r.total),
                ...labels.map((x) => cell(r.quality[x] ?? 0, r.quality[x] ?? '')),
              ])}
            />
          </>
        )}
      </Card>
    </div>
  );
}
